'use client';

/**
 * The standard file list for `FileDropZone` (LF-4642). One row renderer for
 * every surface, replacing the app's 4+ bespoke list styles. Pass `renderItem`
 * to override a row for a caller with bespoke needs (e.g. Intake's source badge).
 *
 * ## Long lists are bounded (VALE-1407)
 *
 * The list had no height bound of any kind, so a bulk upload rendered every
 * file as a full row and simply grew: thirty-six documents is a ~2,300px
 * column that pushes the dialog's own actions off the bottom of the screen,
 * and the reader cannot reach the "Analyse" button without scrolling past
 * every row. This is the shared list behind roughly eighteen upload surfaces,
 * so it is fixed once here rather than per caller.
 *
 * Past {@link DEFAULT_MAX_VISIBLE} rows the list caps its height and scrolls
 * inside, showing every row and offering no control: a button in front of a
 * scroll box is a click that buys the reader nothing, since they scroll either
 * way. At or below that count the markup is byte-identical to what it has
 * always rendered, so no existing surface moves.
 *
 * The bounding itself lives in `CollapsibleRows`, because the same problem
 * turned up on lists that are not `FileDropZone`'s — the AI import step's own
 * two lists and the AI review document list — and a second copy of it would
 * have drifted.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import {
  CollapsibleRows,
  DEFAULT_MAX_VISIBLE
} from '@/components/ui/collapsible-rows';
import { FileDropZoneItem } from './file-drop-zone-item';
import type { UploadFile } from './types';

export { DEFAULT_MAX_VISIBLE };

interface FileDropZoneListProps {
  files: UploadFile[];
  onRemove?: (id: string) => void;
  onRetry?: (id: string) => void;
  readOnly?: boolean;
  renderItem?: (file: UploadFile) => ReactNode;
  /**
   * Rows to show before collapsing the remainder. Defaults to
   * {@link DEFAULT_MAX_VISIBLE}; pass `Infinity` for a surface that genuinely
   * wants every row laid out (a print view, a short fixed set).
   */
  maxVisible?: number;
  className?: string;
}

export function FileDropZoneList({
  files,
  onRemove,
  onRetry,
  readOnly,
  renderItem,
  maxVisible = DEFAULT_MAX_VISIBLE,
  className
}: FileDropZoneListProps) {
  if (files.length === 0) return null;

  return (
    <CollapsibleRows
      maxVisible={maxVisible}
      onOpen="scroll"
      noun="file"
      className={cn('flex flex-col gap-2', className)}
      containerProps={{ role: 'list', 'aria-label': 'Selected files' }}
      rows={files.map((file) => (
        <div key={file.id} role="listitem">
          {renderItem ? (
            renderItem(file)
          ) : (
            <FileDropZoneItem
              file={file}
              onRemove={onRemove}
              onRetry={onRetry}
              readOnly={readOnly}
            />
          )}
        </div>
      ))}
    />
  );
}
